import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Container,
    Typography,
    CircularProgress,
    List,
    ListItemText,
    ListItemButton,
    Divider,
    IconButton,
    useMediaQuery,
} from "@mui/material";
import { getAuth } from "firebase/auth";
import { db } from "../../firebase";
import { collection, getDocs, query, limit, orderBy, startAfter } from "firebase/firestore";

import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import InfiniteScroll from "react-infinite-scroller";

import EditIcon from "@mui/icons-material/Edit";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import YouTubeIcon from "@mui/icons-material/YouTube";

function InfoList() {
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const [infoData, setInfoData] = useState([]);

    const [hasMore, setHasMore] = useState(true);
    const [lastVisible, setLastVisible] = useState(null);

    const isSP = useMediaQuery("(max-width:600px)");
    const youtubeWidth = isSP ? 300 : 720;
    const youtubeHeight = isSP ? 168.75 : 405;

    const [open, setOpen] = useState(false);

    const handleTransInfo = (id) => {
        window.scrollTo(0, 0);
        navigate(`/info/${id}`);
    };

    useEffect(() => {
        const unsubscribe = getAuth().onAuthStateChanged(async (userLogin) => {
            if (userLogin) {
                await fetchData();
            } else {
                window.scrollTo(0, 0);
                navigate("/login", { replace: true });
            }
        });
        return () => unsubscribe();
    }, []);

    const fetchData = async () => {
        const infoRef = collection(db, "info");

        try {
            const q = query(infoRef, orderBy("updateAt", "desc"), limit(10));
            const infoSnap = await getDocs(q);

            if (infoSnap.empty) {
                setHasMore(false);
                setLoading(false);
                return;
            }

            const infos = [];
            infoSnap.forEach((doc) => {
                const timestamp = doc.data().updateAt.seconds;
                const date = new Date(timestamp * 1000);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const hours = String(date.getHours()).padStart(2, "0");
                const minutes = String(date.getMinutes()).padStart(2, "0");
                const seconds = String(date.getSeconds()).padStart(2, "0");
                const formattedDate = `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;

                infos.push({
                    id: doc.data().id,
                    title: doc.data().title,
                    description: doc.data().description,
                    embedCode: doc.data().embedCode,
                    youtubeURL: doc.data().youtubeURL,
                    updateAt: formattedDate,
                });
            });
            setInfoData(infos);

            setLastVisible(infoSnap.docs[infoSnap.docs.length - 1]);
        } catch (error) {
            console.log("error: ", error);
        }

        setLoading(false);
    };

    const fetchMoreData = async () => {
        const infoRef = collection(db, "info");

        try {
            const q = query(infoRef, orderBy("updateAt", "desc"), limit(10), startAfter(lastVisible));
            const infoSnap = await getDocs(q);

            if (infoSnap.empty) {
                setHasMore(false);
                return;
            }

            const infos = [];
            infoSnap.forEach((doc) => {
                const timestamp = doc.data().updateAt.seconds;
                const date = new Date(timestamp * 1000);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const hours = String(date.getHours()).padStart(2, "0");
                const minutes = String(date.getMinutes()).padStart(2, "0");
                const seconds = String(date.getSeconds()).padStart(2, "0");
                const formattedDate = `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;

                infos.push({
                    id: doc.data().id,
                    title: doc.data().title,
                    description: doc.data().description,
                    embedCode: doc.data().embedCode,
                    youtubeURL: doc.data().youtubeURL,
                    updateAt: formattedDate,
                });
            });
            setInfoData((prevData) => [...prevData, ...infos]);

            setLastVisible(infoSnap.docs[infoSnap.docs.length - 1]);
        } catch (error) {
            console.log("error: ", error);
        }
    };

    if (loading) {
        return (
            <Box
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "80vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div>
            <Helmet>
                <title>Info List - SOG Member Site</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <Container maxWidth="1280" sx={{ display: { xs: { maxWidth: 0 } }, maxWidth: 1280 }}>
                <Box
                    sx={{ mt: 10 }}
                    style={{
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Typography variant="h6">お知らせ</Typography>
                </Box>

                <InfiniteScroll
                    pageStart={0}
                    loadMore={fetchMoreData}
                    hasMore={hasMore}
                    loader={
                        <Box
                            sx={{ m: 1 }}
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            key={0}
                        >
                            <CircularProgress />
                        </Box>
                    }
                >
                    <List sx={{ mt: 2 }}>
                        <Divider />
                        {infoData.map((item, i) => (
                            <React.Fragment key={i}>
                                <ListItemButton onClick={() => setOpen(open === i ? -1 : i)}>
                                    <ListItemText
                                        primary={
                                            <Typography
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <span>{item.title}</span>
                                                <span
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <IconButton onClick={() => handleTransInfo(item.id)} sx={{ mr: 1 }}>
                                                        <EditIcon />
                                                    </IconButton>
                                                    {open === i ? <ExpandLess /> : <ExpandMore />}
                                                </span>
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography
                                                color="text.secondary"
                                                variant="body2"
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "right",
                                                }}
                                            >
                                                <span>{item.updateAt}</span>
                                            </Typography>
                                        }
                                    />
                                </ListItemButton>
                                {open === i && (
                                    <Box sx={{ px: 2, pt: 2 }}>
                                        <Typography variant="body2">{item.description}</Typography>

                                        {item.youtubeURL && (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    mt: 2,
                                                }}
                                            >
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    onClick={() => window.open(item.youtubeURL, "_blank", "noopener,noreferrer")}
                                                    sx={{ textTransform: "none", display: "flex", alignItems: "center" }}
                                                >
                                                    <YouTubeIcon sx={{ mr: 1 }} />
                                                    YouTubeで見る
                                                </Button>
                                            </Box>
                                        )}

                                        {item.embedCode && (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    overflowX: "auto",
                                                }}
                                            >
                                                <Box sx={{ width: "100%", maxWidth: "100%", display: "flex", justifyContent: "center" }}>
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: item.embedCode
                                                                .replace(/width="\d+"/g, `width="${youtubeWidth}"`)
                                                                .replace(/height="\d+"/g, `height="${youtubeHeight}"`),
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </React.Fragment>
                        ))}
                    </List>
                </InfiniteScroll>
            </Container>
        </div>
    );
}

export default InfoList;
